import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { BsArrowLeft } from "react-icons/bs";
import Button from "../../components/Button";
import MovieDetailsContent from "../../components/MovieDetailsContent";
import PageLoader from "../../components/PageLoader";
import SectionMessage from "../../components/SectionMessage";
import { getMovieDetails } from "../../services/tmdb";
import { getBackdropUrl } from "../../utils/movie";

const Page = styled.main`
  display: grid;
  gap: 2rem;
  padding: 6.5rem 0 4rem;
`;

const Hero = styled.section`
  position: relative;
  min-height: 320px;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(180deg, rgba(10, 10, 10, 0.32), rgba(10, 10, 10, 0.95)),
    linear-gradient(90deg, rgba(10, 10, 10, 0.8), rgba(10, 10, 10, 0.25)),
    url(${({ $background }) => $background});
  background-size: cover;
  background-position: center;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.9rem;
  width: min(1180px, calc(100% - 1.5rem));
  min-height: 320px;
  margin: 0 auto;
  align-content: end;
  padding: 2rem 0;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.2rem, 5vw, 4rem);
`;

const HeroCopy = styled.p`
  margin: 0;
  max-width: 60ch;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.75;
`;

const Content = styled.div`
  width: min(1180px, calc(100% - 1.5rem));
  margin: 0 auto;
`;

function Filme() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", movie: null });

  const loadMovie = useCallback(async () => {
    try {
      setState({ loading: true, error: "", movie: null });
      const movie = await getMovieDetails(id);
      setState({ loading: false, error: "", movie });
    } catch (error) {
      setState({ loading: false, error: "Nao conseguimos abrir este filme agora.", movie: null });
    }
  }, [id]);

  useEffect(() => {
    loadMovie();
  }, [loadMovie]);

  const handleSelectMovie = useCallback(
    (movieId) => {
      navigate(`/filme/${movieId}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [navigate]
  );

  if (state.loading) {
    return <PageLoader label="Carregando a pagina do filme..." />;
  }

  if (state.error || !state.movie) {
    return (
      <Page>
        <SectionMessage
          title="Filme indisponivel"
          description={state.error || "Nao encontramos este titulo no momento."}
          actionLabel="Voltar para a home"
          onAction={() => navigate("/")}
        />
      </Page>
    );
  }

  const background = getBackdropUrl(state.movie.backdropPath);

  return (
    <Page>
      <Hero>
        <HeroBackground $background={background} aria-hidden />
        <HeroContent>
          <Button variant="secondary" icon={<BsArrowLeft />} onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <HeroTitle>{state.movie.title}</HeroTitle>
          <HeroCopy>
            Experiencia completa com trailer, favoritos, elenco principal e recomendacoes relacionadas ao titulo que voce escolheu.
          </HeroCopy>
        </HeroContent>
      </Hero>
      <Content>
        <MovieDetailsContent movie={state.movie} onSelectMovie={handleSelectMovie} />
      </Content>
    </Page>
  );
}

export default Filme;
